import React from 'react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';

export function Table({ children, className, bordered = true }) {
  return (
    <div
      data-portica-role="table"
      className={clsx('overflow-x-auto', bordered && 'border', className)}
      style={bordered ? { borderColor: 'var(--card-border)', borderRadius: 'var(--radius-md)' } : undefined}
    >
      <table className="min-w-full divide-y" style={{ borderColor: 'var(--color-border)', tableLayout: 'auto' }}>
        {children}
      </table>
    </div>
  );
}

export function TableHead({ children }) {
  return <thead style={{ backgroundColor: 'var(--table-header-bg)' }}>{children}</thead>;
}

export function TableBody({ children }) {
  return <tbody className="divide-y" style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>{children}</tbody>;
}

export function TableRow({ children, className, onClick, ...props }) {
  return (
    <tr
      className={clsx(
        'transition-colors',
        onClick && 'cursor-pointer',
        className
      )}
      style={onClick ? { '--hover-bg': 'var(--color-surface-sunken)', height: '48px' } : { height: '48px' }}
      onMouseEnter={onClick ? (e) => { e.currentTarget.style.backgroundColor = 'var(--color-surface-sunken)'; } : undefined}
      onMouseLeave={onClick ? (e) => { e.currentTarget.style.backgroundColor = ''; } : undefined}
      onClick={onClick}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter') onClick(e); } : undefined}
      {...props}
    >
      {children}
    </tr>
  );
}

// IDI-119 / IDI-200 (column half): sticky prop pins a cell to the right edge
// of the Table's `overflow-x-auto` container so a pinned column (e.g. Actions)
// stays reachable at narrow viewports.
//
// Falsifier from BUILD SCOPE: at 1152px the ACTIONS column right-edge was
// 1274px and off-screen; with `sticky`, the column pins at right: 0 of the
// scroll container.
//
// Backgrounds AND the left border separator live in index.css via
// [data-sticky="true"] so the sticky cell tracks its row's hover state and
// so that we honour design-system §R2 (surfaces are flat; shadows only on
// overlays — the separator is a border, not a cast shadow).
//
// Opt-in per cell: consumer marks the last <TableHeader> AND every matching
// <TableCell> with `sticky`. Non-sticky consumers are untouched.
const STICKY_LAYOUT = {
  position: 'sticky',
  right: 0,
};

// IDI-479: opt-in click-to-sort. Pass `sortKey`, `sortDirection`
// ('asc' | 'desc' | null) and `onSort(sortKey)` — usually by spreading
// `getSortProps(key)` from `useTableSort`. Without `onSort` the header renders
// exactly as before (plain text, no aria-sort).
const SORT_ARIA = { asc: 'ascending', desc: 'descending' };

// IDI-508: exported so pages that draw their own <th> (Item Catalog) show the
// same indicator.
export function SortIndicator({ direction }) {
  // Drawn as SVG so the indicator adds no textContent: header labels read the
  // same whether or not the column is sorted.
  const active = direction === 'asc' || direction === 'desc';
  const path = direction === 'asc'
    ? 'M5 2l5 7H0z' // ▲
    : direction === 'desc'
      ? 'M5 12L0 5h10z' // ▼
      : 'M5 0l4 5H1zM5 14l-4-5h8z'; // faint neutral ↕ on inactive sortable columns
  return (
    <svg
      aria-hidden="true"
      data-sort-indicator={active ? direction : 'none'}
      viewBox="0 0 10 14"
      width="8"
      height="11"
      fill="currentColor"
      style={active ? undefined : { opacity: 0.35 }}
    >
      <path d={path} />
    </svg>
  );
}

export function TableHeader({
  children,
  className,
  align = 'left',
  sticky = false,
  sortKey,
  sortDirection = null,
  onSort,
}) {
  const sortable = typeof onSort === 'function';
  const activeDir = sortDirection === 'asc' || sortDirection === 'desc' ? sortDirection : null;
  return (
    <th
      data-sticky={sticky ? 'true' : undefined}
      aria-sort={sortable ? SORT_ARIA[activeDir] || 'none' : undefined}
      data-sort-key={sortable ? sortKey : undefined}
      className={clsx(
        'px-4 py-3 text-xs uppercase tracking-wider align-middle text-left',
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        className
      )}
      style={{
        color: 'white',
        fontFamily: 'var(--font-display)',
        fontWeight: 500,
        ...(sticky ? { ...STICKY_LAYOUT, zIndex: 2 } : null),
      }}
    >
      {sortable ? (
        <button
          type="button"
          onClick={() => onSort(sortKey)}
          className={clsx(
            'inline-flex items-center gap-1 uppercase tracking-wider cursor-pointer select-none',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-1 rounded-sm',
            align === 'right' && 'flex-row-reverse'
          )}
          style={{
            background: 'transparent',
            border: 0,
            padding: 0,
            color: 'inherit',
            font: 'inherit',
            letterSpacing: 'inherit',
          }}
        >
          <span>{children}</span>
          <SortIndicator direction={activeDir} />
        </button>
      ) : (
        children
      )}
    </th>
  );
}

export function TableCell({ children, className, align = 'left', href, hrefState, sticky = false, ...rest }) {
  // IDI-157: hrefState is router state for the cell link (origin for Back).
  return (
    <td
      data-sticky={sticky ? 'true' : undefined}
      className={clsx(
        'px-4 py-3 text-sm whitespace-nowrap align-middle text-left',
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        className
      )}
      style={{
        color: 'var(--color-text-primary)',
        ...(sticky ? { ...STICKY_LAYOUT, zIndex: 1 } : null),
      }}
      {...rest}
    >
      {href ? (
        <Link
          to={href}
          state={hrefState}
          className="hover:underline"
          onClick={(e) => e.stopPropagation()}
          style={{ color: 'inherit', textDecoration: 'none' }}
        >
          {children}
        </Link>
      ) : (
        children
      )}
    </td>
  );
}
